'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';

export function CTASection() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section className="relative overflow-hidden py-24 lg:py-32">
      <div className="absolute inset-0 bg-mesh-1" />
      <div className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/5 blur-3xl" />

      <div
        ref={containerRef}
        className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="relative overflow-hidden rounded-3xl border border-border bg-card p-4 sm:p-6 md:p-8 text-center shadow-2xl lg:p-16"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-accent/5 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-dots opacity-20" />

          <div className="relative">
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={isInView ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.2, type: 'spring' }}
              className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-accent/10"
            >
              <Shield className="h-10 w-10 text-accent" strokeWidth={1.5} />
            </motion.div>

            {/* Headline */}
            <h2 className="mt-8 font-display text-3xl tracking-tight-display text-foreground sm:text-4xl lg:text-5xl">
              From annual assessment to{' '}
              <br className="hidden sm:block" />
              <span className="italic text-accent">
                continuous compliance posture.
              </span>
            </h2>

            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
              Structured, measurable, and defensible — CAF-Pilot gives CNI
              operators a repeatable way to run CAF and own the result.
            </p>

            {/* Benefits */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              {[
                '7-day free trial',
                'No credit card required',
                'Setup in minutes',
              ].map((benefit) => (
                <div
                  key={benefit}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  {benefit}
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button
                size="lg"
                className="btn-glow h-14 bg-accent px-8 font-heading text-base font-semibold text-accent-foreground hover:bg-accent/90"
                asChild
              >
                <Link href="/register">
                  Start your 7-day free trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
          </div>
            </div>


          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full border border-accent/20 bg-accent/5" />
          <div className="absolute -bottom-8 -left-8 h-32 w-32 rounded-full border border-accent/10 bg-accent/5" />
        </motion.div>
      </div>
    </section>
  );
}
